import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
const apiUrl = process.env.REACT_APP_API_URL;
function AdminViewBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // State for lessons per batch (object with batchId as key)
  const [lessonsByBatch, setLessonsByBatch] = useState({});
  const [lessonsLoading, setLessonsLoading] = useState({});
  const [lessonsError, setLessonsError] = useState({}); // Added lessonsError state
  const [expandedBatches, setExpandedBatches] = useState({}); // Track expanded rows
  const [courseFilter, setCourseFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Memoize fetchBatches with useCallback
  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in to view batches.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/batches?nocache=${Date.now()}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setBatches(response.data.batches);
      } else {
        setError(response.data.message || 'Failed to fetch batches.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching batches.');
    } finally {
      setLoading(false);
    }
  }, []);
  // Fetch lessons for a specific batch
  const fetchLessons = useCallback(async (batchId) => {
    setLessonsLoading((prev) => ({ ...prev, [batchId]: true }));
    setLessonsByBatch((prev) => ({ ...prev, [batchId]: [] }));
    setLessonsError((prev) => ({ ...prev, [batchId]: '' })); // Reset error for this batch
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        console.error('No token for lessons fetch');
        setLessonsError((prev) => ({ ...prev, [batchId]: 'Authentication error' }));
        return;
      }
      console.log(`Fetching lessons for batchid: ${batchId}`);
      const response = await axios.get(`${apiUrl}/api/admin/batch-lessons/${batchId}?nocache=${Date.now()}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      console.log('API Response for lessons:', response.data);
      if (response.data.success) {
        setLessonsByBatch((prev) => ({ ...prev, [batchId]: response.data.lessons || [] }));
      } else {
        setLessonsError((prev) => ({ ...prev, [batchId]: response.data.message || 'Failed to fetch lessons' }));
      }
    } catch (err) {
      setLessonsError((prev) => ({ ...prev, [batchId]: err.response?.data?.message || 'Error fetching lessons' }));
    } finally {
      setLessonsLoading((prev) => ({ ...prev, [batchId]: false }));
    }
  }, []);
  // Delete lesson
  const deleteLesson = async (batchId, lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        alert('Authentication error');
        return;
      }
      const response = await axios.delete(`${apiUrl}/api/admin/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        fetchLessons(batchId); // Refresh lessons
      } else {
        alert(response.data.message || 'Failed to delete lesson.');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting lesson.');
    }
  };
  // Toggle lessons expansion for a batch
  const toggleLessons = (batchId) => {
    if (expandedBatches[batchId]) {
      setExpandedBatches((prev) => ({ ...prev, [batchId]: false }));
    } else {
      setExpandedBatches((prev) => ({ ...prev, [batchId]: true }));
      if (!lessonsByBatch[batchId] && !lessonsLoading[batchId]) {
        fetchLessons(batchId);
      }
    }
  };
  // Use useEffect for batches
  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);
  // Unique course names for the filter dropdown
  const courseOptions = useMemo(() => {
    const names = batches.map((batch) => batch.course_name).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [batches]);
  // Batches filtered by selected course and batch name search
  const filteredBatches = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return batches.filter((batch) => {
      const matchesCourse = !courseFilter || batch.course_name === courseFilter;
      const matchesSearch = !term || (batch.batchname || '').toLowerCase().includes(term);
      return matchesCourse && matchesSearch;
    });
  }, [batches, courseFilter, searchTerm]);
  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">View Batches</span>
                </li>
              </ul>
            </div>
            <Link to="/admin-add-batch" className="btn btn-primary">
              Add Batch
            </Link>
          </div>
          <div className="card overflow-hidden">
            <div className="card-header p-16 flex-between flex-wrap gap-3">
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <select
                  className="form-select w-auto"
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                >
                  <option value="">All Courses</option>
                  {courseOptions.map((course) => (
                    <option key={course} value={course}>
                      {course}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control w-auto"
                  placeholder="Search batch name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="card-body p-0 overflow-x-auto">
              {loading && (
                <div className="alert alert-info mb-0" role="alert">
                  Loading batches...
                </div>
              )}
              {error && (
                <div className="alert alert-danger mb-0" role="alert">
                  {error}
                </div>
              )}
              {!loading && !error && batches.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No batches found.
                </div>
              )}
              {!loading && !error && batches.length > 0 && filteredBatches.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No batches match your filters.
                </div>
              )}
              {!loading && !error && filteredBatches.length > 0 && (
                <table id="batchTable" className="table table-striped">
                  <thead>
                    <tr>
                      <th className="fixed-width">
                        <div className="form-check">
                          <input
                            className="form-check-input border-gray-200 rounded-4"
                            type="checkbox"
                            id="selectAll"
                          />
                        </div>
                      </th>
                      <th className="h6 text-gray-300">Course Name</th>
                      <th className="h6 text-gray-300">Batch Name</th>
                      <th className="h6 text-gray-300">No. of Students</th>
                      <th className="h6 text-gray-300">No. of Lessons</th>
                      <th className="h6 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBatches.map((batch) => (
                      <React.Fragment key={batch.id}>
                        <tr>
                          <td className="fixed-width">
                            <div className="form-check">
                              <input
                                className="form-check-input border-gray-200 rounded-4"
                                type="checkbox"
                              />
                            </div>
                          </td>
                          <td>
                            <span className="h6 mb-0 fw-medium text-gray-300">
                              {batch.course_name}
                            </span>
                          </td>
                          <td>
                            <span className="h6 mb-0 fw-medium text-gray-300">
                              {batch.batchname.length > 30 ? `${batch.batchname.substring(0, 30)}...` : batch.batchname}
                            </span>
                            {Number(batch.rolling_enrollment) === 1 && (
                              <span
                                className="badge bg-main-600 text-white ms-2 rounded-pill text-13 fw-medium"
                                title="Students only see lessons dated on/after the day they joined"
                              >
                                Rolling
                              </span>
                            )}
                          </td>
                          <td>
                            <Link
                              to={`/admin-batch-students/${batch.id}`}
                              className="bg-main-50 text-main-600 py-2 px-14 rounded-pill flex-align gap-8 d-inline-flex hover-bg-main-600 hover-text-white"
                            >
                              <i className="ph ph-users" />
                              {batch.student_count}
                            </Link>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn bg-main-50 text-main-600 py-2 px-14 rounded-pill flex-align gap-8 d-inline-flex hover-bg-main-600 hover-text-white"
                              onClick={() => toggleLessons(batch.id)}
                            >
                              <i className="ph ph-book-open" />
                              {batch.lesson_count || 0}
                              <i className={`ph ${expandedBatches[batch.id] ? 'ph-caret-up' : 'ph-caret-down'}`} />
                            </button>
                          </td>
                          <td>
                            <Link to={`/admin-edit-batch/${batch.id}`} className="bg-main-50 text-main-600 py-2 px-14 rounded-pill hover-bg-main-600 hover-text-white">
                              Edit
                            </Link>
                            <Link to={`/admin-add-lesson/${batch.id}`} className="bg-main-50 text-main-600 py-2 px-14 rounded-pill hover-bg-main-600 hover-text-white ml-2">
                              Add Lessons
                            </Link>
                          </td>
                        </tr>
                        {expandedBatches[batch.id] && (
                          <tr>
                            <td colSpan="6">
                              <div className="p-3 bg-light">
                                {lessonsLoading[batch.id] && (
                                  <div className="alert alert-info">Loading lessons...</div>
                                )}
                                {lessonsError[batch.id] && (
                                  <div className="alert alert-danger">{lessonsError[batch.id]}</div>
                                )}
                                {lessonsByBatch[batch.id] && lessonsByBatch[batch.id].length === 0 && !lessonsLoading[batch.id] && !lessonsError[batch.id] && (
                                  <div className="alert alert-warning">No lessons found.</div>
                                )}
                                {lessonsByBatch[batch.id] && lessonsByBatch[batch.id].length > 0 && !lessonsLoading[batch.id] && !lessonsError[batch.id] && (
                                  <div className="table-responsive">
                                    <table className="table table-striped mb-0">
                                      <thead>
                                        <tr>
                                          <th>Title</th>
                                          <th>Date</th>
                                          <th>Summary</th>
                                          <th>YouTube Link</th>
                                          <th>Files</th>
                                          <th>Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {lessonsByBatch[batch.id].map((lesson) => (
                                          <tr key={lesson.id}>
                                            <td>{lesson.title || 'Untitled'}</td>
                                            <td>{lesson.lesson_date ? new Date(lesson.lesson_date).toLocaleDateString() : 'N/A'}</td>
                                            <td>{lesson.summary ? (lesson.summary.length > 50 ? `${lesson.summary.substring(0, 50)}...` : lesson.summary) : 'N/A'}</td>
                                            <td>
                                              {lesson.youtube_link ? (
                                                <a href={lesson.youtube_link} target="_blank" rel="noopener noreferrer" className="text-primary">
                                                  View Video
                                                </a>
                                              ) : (
                                                'N/A'
                                              )}
                                            </td>
                                            <td>
                                              {lesson.files && lesson.files.length > 0 ? (
                                                <ul>
                                                  {lesson.files.map((file, idx) => (
                                                    <li key={idx}>
                                                      <a href={`${apiUrl}/${file.filepath}`} target="_blank" rel="noopener noreferrer">
                                                        {file.title || 'Untitled File'}
                                                      </a>
                                                    </li>
                                                  ))}
                                                </ul>
                                              ) : 'No files'}
                                            </td>
                                            <td>
                                              <Link to={`/admin-edit-lesson/${lesson.id}`} className="btn btn-sm btn-primary me-2">Edit</Link>
                                              <button className="btn btn-sm btn-danger" onClick={() => deleteLesson(batch.id, lesson.id)}>Delete</button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card-footer flex-between flex-wrap">
              <span className="text-gray-900">
                Showing {filteredBatches.length} of {batches.length} entries
              </span>
              <ul className="pagination flex-align flex-wrap">
                <li className="page-item active">
                  <button
                    className="page-link h-44 w-44 flex-center text-15 rounded-8 fw-medium"
                  >
                    1
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
export default AdminViewBatches;